import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import './NewUserLoginPage.scss';
import { Link } from 'react-router-dom';

// Asset imports
import accoladeLogo from '../../assets/Logo-Accolade-dark.svg';
import brandLogos from '../../assets/Tri-Brand_Maroline-logos-dark.svg';
import languageIcon from '../../assets/icons/language-icon-gray.svg';
import showPasswordIcon from '../../assets/icons/show-password-icon.svg';

const NewUserLoginPage: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const { login } = useAuth();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isFormValid = formData.username && formData.password;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.username || !formData.password) {
      setError(t('loginForm.error.allFieldsRequired'));
      return;
    }

    try {
      setLoading(true);
      await login(formData);
    } catch (err) {
      setError(t('loginForm.error.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="new-user-login-page">
      <button
          className="language-toggle"
          onClick={toggleLanguage}
          aria-label={t('nav.changeLanguage')}
        >
          <img src={languageIcon} alt="" />
          <span>{i18n.language.toUpperCase()}</span>
        </button>
      <div className="content-container">
        <header className="header-auth">
          <p className="welcomeText">{t('authPage.welcomeTo')}</p>
          <Link to="/auth">
            <img src={accoladeLogo} alt={t('altText.accoladeLogo')} className="accoladeLogo" />
          </Link>
        </header>

        <div className="login-form-container">
          <div className="login-form__header">
            <h2 className="login-form__title">{t('newUserLoginPage.title')}</h2>
            <p className="login-form__description">{t('newUserLoginPage.description')}</p>
          </div>
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">{t('newUserLoginPage.username')}</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{t('newUserLoginPage.password')}</label>
              <div className="password-input-container">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                />
                <button 
                  type="button" 
                  className="show-password-button" 
                  onClick={togglePasswordVisibility}
                  aria-label={showPassword ? t('loginForm.hidePassword') : t('loginForm.showPassword')}
                >
                  <img src={showPasswordIcon} alt="" />
                </button>
              </div>
              <Link to="/auth" state={{ showForgotPassword: true }} className="forgot-password">
                {t('newUserLoginPage.forgotPassword')}
              </Link>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className={`login-button ${isFormValid ? 'valid' : ''}`}
              disabled={loading}
            >
              {loading
                ? t('loginForm.loggingIn')
                : t('loginForm.login')}
            </button>
          </form>
        </div>
      </div>
      <footer className="footer-auth">
          <img src={brandLogos} alt={t('altText.brandLogos')} className="brandLogos" />
          <Link to="/guest-contact" className="contact-link">
            {t('authPage.needHelp')} <span className="underline-hover">{t('authPage.contactUs')}</span>
          </Link>
        </footer>
    </div>
  );
};

export default NewUserLoginPage;
